import { Checkbox, FormControlLabel, FormHelperText } from "~/common/mui";
import { Controller, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { StyledFormControl } from "~/features/cart/B2CRequestPage/components/Checkboxes";
import { makeExternallyStyledElement } from "~/features/cms/utils/styles";
import type { SchaefflerFormCheckboxFieldDTO } from "~/features/cms/types/dto";

import { SeeMoreLongText } from "~/common/components/SeeMoreLongText";

export type SchaefflerFormCheckboxFieldProps = Omit<SchaefflerFormCheckboxFieldDTO, "type"> & {
  className?: string;
};

export const SchaefflerFormCheckboxField = (props: SchaefflerFormCheckboxFieldProps) => {
  const { i18n, t } = useTranslation();
  const { control } = useFormContext();

  const hasMore = !!props.fullTitle;

  const renderLabel = (error: boolean) => {
    if (hasMore) {
      return (
        <SeeMoreLongText
          shortText={<Trans i18nKey={props.title} i18n={i18n} />}
          fullText={<Trans i18nKey={props.fullTitle} i18n={i18n} components={{ Link: <a />, a: <a /> }} />}
          required={props.required}
          error={error}
        />
      );
    } else {
      return <Trans i18nKey={props.title} components={{ Link: <a />, a: <a /> }} />;
    }
  };

  return (
    <Controller
      control={control}
      name={props.name}
      defaultValue={"0"}
      rules={{
        validate: {
          value: (value) => {
            if (!props.required) {
              return true;
            }
            return value === "1" || t("page.scandmc.form.error.required");
          },
        },
      }}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
        <ExternallyStyledFormControl error={!!error} required={props.required} rootStyles={props.rootStyles}>
          <FormControlLabel
            label={renderLabel(!!error)}
            required={!hasMore && props.required}
            slotProps={{
              typography: {
                sx: {
                  minWidth: 0,
                  wordBreak: "break-word",
                },
              },
            }}
            control={
              <Checkbox
                {...field}
                required={props.required}
                checked={value === "1"}
                onChange={(_, value) => onChange(value ? "1" : "0")}
              />
            }
          />
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </ExternallyStyledFormControl>
      )}
    />
  );
};

const ExternallyStyledFormControl = makeExternallyStyledElement(StyledFormControl);
